import React, { useState, useEffect } from 'react';
import { studentMasterAPI } from '../../services/api';
import './Modal.css';

const StudentDetailsModal = ({ studentId, onClose }) => {
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStudentDetails();
  }, [studentId]);

  const loadStudentDetails = async () => {
    setIsLoading(true);
    try {
      const response = await studentMasterAPI.getStudentDetails(studentId);
      if (response.success) {
        setDetails(response.data);
      } else {
        console.error('Failed to load student details:', response.message);
      }
    } catch (error) {
      console.error('Error loading student details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format IC for display with dashes (XXXXXX-XX-XXXX)
  // Backend already handles masking based on permissions
  const formatIC = (ic) => {
    if (!ic) return '-';
    if (ic.length < 4) return ic; // Already masked (last 4 digits only)
    if (ic.length === 12) {
      // Format 12-digit IC: XXXXXX-XX-XXXX
      return `${ic.substring(0, 6)}-${ic.substring(6, 8)}-${ic.substring(8)}`;
    }
    // For any other format, return as-is
    return ic;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Butiran Pelajar</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading">Memuatkan data...</div>
          ) : details ? (
            <>
              {/* Student Basic Information */}
              <div className="form-section">
                <h3>Maklumat Asas</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nama</label>
                    <div className="info-value">{details.student?.name || '-'}</div>
                  </div>
                  <div className="info-item">
                    <label>No. KP</label>
                    <div className="info-value">{formatIC(details.student?.ic)}</div>
                  </div>
                  <div className="info-item">
                    <label>Tarikh Lahir</label>
                    <div className="info-value">
                      {details.student?.dob ? new Date(details.student.dob).toLocaleDateString('ms-MY') : '-'}
                    </div>
                  </div>
                  <div className="info-item">
                    <label>Jantina</label>
                    <div className="info-value">
                      {details.student?.jantina === 'L' ? 'Lelaki' : details.student?.jantina === 'P' ? 'Perempuan' : '-'}
                    </div>
                  </div>
                  <div className="info-item">
                    <label>Status Warganegara</label>
                    <div className="info-value">{details.student?.citizenStatus || '-'}</div>
                  </div>
                </div>
              </div>

              {/* Enrollment History */}
              <div className="form-section">
                <h3>Sejarah Pendaftaran</h3>
                {details.enrollments && details.enrollments.length > 0 ? (
                  <div className="table-responsive">
                    <table className="details-table">
                      <thead>
                        <tr>
                          <th>Tahun Pendaftaran</th>
                          <th>Sekolah</th>
                          <th>Tahun</th>
                          <th>Kelas</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.enrollments.map((enroll, index) => (
                          <tr key={index}>
                            <td>{enroll.enrollYear}</td>
                            <td>{enroll.schoolName} ({enroll.schoolCode})</td>
                            <td>{enroll.tahun || '-'}</td>
                            <td>{enroll.class || '-'}</td>
                            <td>
                              <span className={`status-badge ${enroll.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                                {enroll.status === 'ACTIVE' ? 'Aktif' :
                                 enroll.status === 'GRADUATED' ? 'Tamat Pengajian' :
                                 enroll.status === 'TRANSFERRED' ? 'Berpindah' :
                                 'Tidak Aktif'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Tiada sejarah pendaftaran</p>
                )}
              </div>

              {/* Activity/Uniform History */}
              <div className="form-section">
                <h3>Sejarah Aktiviti/Uniform</h3>
                {details.activities && details.activities.length > 0 ? (
                  <div className="table-responsive">
                    <table className="details-table">
                      <thead>
                        <tr>
                          <th>Aktiviti</th>
                          <th>Tahun</th>
                          <th>Saiz</th>
                          <th>Catatan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.activities.map((activity, index) => (
                          <tr key={index}>
                            <td>{activity.activityName}</td>
                            <td>{activity.activityYear}</td>
                            <td>
                              {[
                                activity.bajuPutih && `Baju Putih: ${activity.bajuPutih}`,
                                activity.seluarPanjang && `Seluar Panjang: ${activity.seluarPanjang}`,
                                activity.seluarPendek && `Seluar Pendek: ${activity.seluarPendek}`,
                                activity.bajuKurung && `Baju Kurung: ${activity.bajuKurung}`,
                                activity.kainKurung && `Kain Kurung: ${activity.kainKurung}`,
                                activity.pinafore && `Pinafore: ${activity.pinafore}`,
                                activity.kasut && `Kasut: ${activity.kasut}`
                              ].filter(Boolean).join(', ') || '-'}
                            </td>
                            <td>-</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data">Tiada sejarah aktiviti/uniform</p>
                )}
              </div>
            </>
          ) : (
            <div className="no-data">Tiada data</div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDetailsModal;
